/* eslint-disable no-param-reassign */
/* eslint-disable no-shadow */
import { groupBy, mapValues, concat, omit, isEqual, find } from 'lodash';

const visible = {
  SOME_VISIBLE: 'some_visible',
  ALL_VISIBLE: 'all_visible',
  NONE_VISIBLE: 'none_visible',
};

// initial state
const getInitialState = () => ({
  // expected format:
  //  errors: [{
  //    message: 'Node should be connected...',
  //    category: 'caseLogic',
  //    field: 'caseLogicDescription',
  //    id: '2',
  //    metadata: { ... }
  //  }]
  errors: [],
  warnings: [],
  warningVisiblility: visible.SOME_VISIBLE,
});
const state = getInitialState();

function isVisible(warning, warningVisiblility) {
  if (warning.metadata.dismissed) {
    return false;
  } else if (warningVisiblility === visible.NONE_VISIBLE) {
    return false;
  } else if (warningVisiblility === visible.SOME_VISIBLE) {
    return warning.metadata.defaultVisibility === undefined ||
           warning.metadata.defaultVisibility === true;
  }
  return true;
}

function assignDismissed(currentList, previousList) {
  // Newly created warnings don't have dismissed flags in them,
  // so copy from previous list of warnings
  currentList.forEach(current => {
    const previous = find(previousList, item =>
      isEqual(omit(current, 'metadata.dismissed'),
              omit(item, 'metadata.dismissed'),
    ));
    if (previous) {
      current.metadata.dismissed = previous.metadata.dismissed;
    }
  });
}

// getters
const getters = {

  allVisibleWarnings(state) {
    return state.warnings.filter(w => isVisible(w, state.warningVisiblility));
  },

  errorsByCategory(state) {
    return groupBy(state.errors, 'category');
  },

  warningsByCategory(state) {
    return groupBy(state.warnings, 'category');
  },

  visibleWarningsByCategory(state) {
    return groupBy(
      state.warnings.filter(w => isVisible(w, state.warningVisiblility)),
      'category',
    );
  },

  errorsByField(state) {
    return groupBy(state.errors, 'field');
  },

  warningsByField(state) {
    return groupBy(state.warnings, 'field');
  },

  visibleWarningsByField(state) {
    return groupBy(
      state.warnings.filter(w => isVisible(w, state.warningVisiblility)),
      'field',
    );
  },

  errorsByFieldAndId(state) {
    return mapValues(groupBy(state.errors, 'field'), fieldGroup => groupBy(fieldGroup, 'id'));
  },

  warningsByFieldAndId(state) {
    return mapValues(groupBy(state.warnings, 'field'), fieldGroup => groupBy(fieldGroup, 'id'));
  },

  visibleWarningsByFieldAndId(state) {
    const warnings = state.warnings.filter(w => isVisible(w, state.warningVisiblility));
    return mapValues(groupBy(warnings, 'field'), fieldGroup => groupBy(fieldGroup, 'id'));
  },
};

// mutations
const mutations = {

  updateErrors(state, errors) {
    if (isEqual(state.errors, errors)) {
      return;
    }
    state.errors = errors;
  },

  updateWarnings(state, warnings) {
    if (isEqual(state.warnings, warnings)) {
      return;
    }
    const previousWarnings = state.warnings;
    state.warnings = warnings;
    assignDismissed(state.warnings, previousWarnings);
  },

  dismiss(state, warning) {
    const index = state.warnings.findIndex(w => w === warning);
    if (index >= 0) {
      warning.metadata.dismissed = true;
      state.warnings.splice(index, 1, warning);
    }
  },

  concatWarnings(state, warnings) {
    if (!warnings.length) {
      return;
    }
    state.warnings = concat(state.warnings, warnings);
  },

  concatErrors(state, errors) {
    if (!errors.length) {
      return;
    }
    state.errors = concat(state.errors, errors);
  },

  reset(state) {
    state.errors = [];
    state.warnings = [];
    state.warningVisiblility = visible.SOME_VISIBLE;
  },

  showSomeWarnings(state) {
    state.warningVisiblility = visible.SOME_VISIBLE;
  },

  showAllWarnings(state) {
    state.warningVisiblility = visible.ALL_VISIBLE;
    state.warnings.forEach(w => {
      w.metadata.dismissed = undefined;
    });
  },

  showNoWarnings(state) {
    state.warningVisiblility = visible.NONE_VISIBLE;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
};
